// src/components/orders/OrderActivity.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { ACTIVITY_MESSAGES } from '../../utils/orderActivityLogger';

const styles = {
  container: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '2px solid #f1f5f9'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0
  },
  activityCount: {
    background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: '600'
  },
  timeline: {
    position: 'relative',
    paddingLeft: '24px'
  },
  timelineItem: {
    position: 'relative',
    paddingBottom: '24px',
    borderLeft: '2px solid #e2e8f0'
  },
  timelineItemLast: {
    borderLeft: '2px solid transparent'
  },
  timelineDot: {
    position: 'absolute',
    left: '-6px',
    top: '8px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: '2px solid white',
    boxShadow: '0 0 0 2px #06C167'
  },
  activityCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    marginLeft: '16px',
    transition: 'all 0.2s ease'
  },
  activityHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px'
  },
  activityTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1d29',
    margin: 0
  },
  activityTime: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  activityUser: {
    fontSize: '12px',
    color: '#4f46e5',
    fontWeight: '500',
    marginBottom: '8px'
  },
  activityDetails: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: '1.5'
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px',
    padding: '4px 0'
  },
  detailLabel: {
    fontWeight: '500',
    color: '#6b7280'
  },
  detailValue: {
    color: '#1a1d29',
    fontWeight: '500'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280'
  },
  activityTypeIcon: {
    marginRight: '8px',
    fontSize: '16px'
  },
  changesList: {
    background: '#f1f5f9',
    borderRadius: '6px',
    padding: '8px 12px',
    marginTop: '8px',
    fontSize: '12px'
  },
  itemsList: {
    background: '#f1f5f9',
    borderRadius: '6px',
    padding: '8px 12px',
    marginTop: '8px'
  },
  itemsListItem: {
    fontSize: '12px',
    color: '#374151',
    marginBottom: '2px'
  }
};

// Activity type icons
const getActivityIcon = (activityType) => {
  const icons = {
    siparis_olusturuldu: '✨',
    siparis_guncellendi: '✏️',
    siparis_silindi: '🗑️',
    siparis_durum_degisti: '🔄',
    odeme_alindi: '💰',
    sevkiyat_olusturuldu: '📦',
    siparis_kapatildi: '✅',
    urun_eklendi: '➕',
    urun_cikarildi: '➖',
    urun_guncellendi: '🔧'
  };
  return icons[activityType] || '📝';
};

// Activity type colors
const getActivityColor = (activityType) => {
  const colors = {
    siparis_olusturuldu: '#10b981',
    siparis_guncellendi: '#f59e0b',
    siparis_silindi: '#ef4444',
    siparis_durum_degisti: '#06C167',
    odeme_alindi: '#059669',
    sevkiyat_olusturuldu: '#3b82f6',
    siparis_kapatildi: '#6b7280',
    urun_eklendi: '#10b981',
    urun_cikarildi: '#ef4444',
    urun_guncellendi: '#f59e0b'
  };
  return colors[activityType] || '#06C167';
};

function OrderActivity({ companyId, orderId, canViewPrices }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId || !orderId) {
      setLoading(false);
      return;
    }

    const activitiesRef = collection(db, 'companies', companyId, 'orders', orderId, 'activities');
    const q = query(activitiesRef, orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activityList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActivities(activityList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching order activities:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId, orderId]);

  const formatActivityDetails = (activity) => {
    const { activityType, details = {} } = activity;

    switch (activityType) {
      case 'siparis_olusturuldu':
        return (
          <div style={styles.activityDetails}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Müşteri:</span>
              <span style={styles.detailValue}>{details.customerName || 'Belirtilmemiş'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Toplam Tutar:</span>
              <span style={styles.detailValue}>
                {canViewPrices ? `${details.totalAmount?.toFixed(2) || '0.00'} ${details.currency === 'USD' ? '$' : '₺'}` : '***'}
              </span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Ürün Sayısı:</span>
              <span style={styles.detailValue}>{details.itemCount || 0} adet</span>
            </div>
          </div>
        );

      case 'siparis_guncellendi':
        return (
          <div style={styles.activityDetails}>
            {details.changes && (
              <div style={styles.changesList}>
                <strong>Değişiklikler:</strong>
                <div style={{ marginTop: '4px' }}>
                  {Object.entries(details.changes).map(([key, value], index) => (
                    <div key={index} style={styles.itemsListItem}>
                      • {key}: {typeof value === 'object' ? JSON.stringify(value) : value}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Değişen Alan Sayısı:</span>
              <span style={styles.detailValue}>{details.changeCount || 0}</span>
            </div>
          </div>
        );

      case 'odeme_alindi':
        return (
          <div style={styles.activityDetails}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Ödeme Tutarı:</span>
              <span style={styles.detailValue}>
                {canViewPrices ? `${details.paymentAmount?.toFixed(2) || '0.00'} ${details.currency === 'USD' ? '$' : '₺'}` : '***'}
              </span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Ödeme Yöntemi:</span>
              <span style={styles.detailValue}>{details.paymentMethod || 'Belirtilmemiş'}</span>
            </div>
            {details.transactionId && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>İşlem ID:</span>
                <span style={styles.detailValue}>{details.transactionId}</span>
              </div>
            )}
          </div>
        );

      case 'siparis_durum_degisti':
        return (
          <div style={styles.activityDetails}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Eski Durum:</span>
              <span style={styles.detailValue}>{details.oldStatus || 'Belirtilmemiş'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Yeni Durum:</span>
              <span style={styles.detailValue}>{details.newStatus || 'Belirtilmemiş'}</span>
            </div>
            {details.reason && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Sebep:</span>
                <span style={styles.detailValue}>{details.reason}</span>
              </div>
            )}
          </div>
        );

      case 'sevkiyat_olusturuldu':
        return (
          <div style={styles.activityDetails}>
            {details.shipmentItems && details.shipmentItems.length > 0 && (
              <div style={styles.itemsList}>
                <strong>Sevk Edilen Ürünler:</strong>
                <div style={{ marginTop: '4px' }}>
                  {details.shipmentItems.map((item, index) => (
                    <div key={index} style={styles.itemsListItem}>
                      • {item.productName}: {item.quantity} adet
                    </div>
                  ))}
                </div>
              </div>
            )}
            {details.cargoCompany && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Kargo Firması:</span>
                <span style={styles.detailValue}>{details.cargoCompany}</span>
              </div>
            )}
            {details.trackingNumber && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Takip Numarası:</span>
                <span style={styles.detailValue}>{details.trackingNumber}</span>
              </div>
            )}
          </div>
        );

      case 'urun_eklendi':
      case 'urun_cikarildi':
      case 'urun_guncellendi':
        const itemKey = activityType === 'urun_eklendi' ? 'addedItems' : 
                       activityType === 'urun_cikarildi' ? 'removedItems' : 'updatedItems';
        const items = details[itemKey] || [];
        
        return (
          <div style={styles.activityDetails}>
            {items.length > 0 && (
              <div style={styles.itemsList}>
                <strong>Ürünler:</strong>
                <div style={{ marginTop: '4px' }}>
                  {items.map((item, index) => (
                    <div key={index} style={styles.itemsListItem}>
                      • {item.productName}: {item.quantity} adet
                      {item.price && ` - ${canViewPrices ? `${item.price.toFixed(2)} ₺/adet` : '***'}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Ürün Sayısı:</span>
              <span style={styles.detailValue}>{details.itemCount || 0}</span>
            </div>
          </div>
        );

      default:
        return (
          <div style={styles.activityDetails}>
            {details.description && <p>{details.description}</p>}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <div>Sipariş hareketleri yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Sipariş Hareketleri</h3>
        <span style={styles.activityCount}>
          {activities.length} hareket
        </span>
      </div>

      {activities.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📝</div>
          <p>Henüz sipariş hareketi bulunmuyor.</p>
          <p>Sipariş üzerinde yapılan tüm işlemler burada görüntülenecek.</p>
        </div>
      ) : (
        <div style={styles.timeline}>
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              style={{
                ...styles.timelineItem,
                ...(index === activities.length - 1 ? styles.timelineItemLast : {})
              }}
            >
              <div
                style={{
                  ...styles.timelineDot,
                  background: getActivityColor(activity.activityType)
                }}
              />
              <div
                style={styles.activityCard}
                onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.background = '#f8fafc'}
              >
                <div style={styles.activityHeader}>
                  <div>
                    <h4 style={styles.activityTitle}>
                      <span style={styles.activityTypeIcon}>
                        {getActivityIcon(activity.activityType)}
                      </span>
                      {ACTIVITY_MESSAGES[activity.activityType] || activity.activityMessage || 'Bilinmeyen İşlem'}
                    </h4>
                    <div style={styles.activityUser}>
                      👤 {activity.userName || activity.userEmail?.split('@')[0] || 'Bilinmeyen Kullanıcı'}
                      {activity.details?.userRole && activity.details.userRole !== 'kullanıcı' && (
                        <span style={{ marginLeft: '8px', textTransform: 'uppercase' }}>
                          ({activity.details.userRole})
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={styles.activityTime}>
                    {activity.formattedDate || 
                     (activity.timestamp?.toDate?.()?.toLocaleString('tr-TR') || 'Tarih bilinmiyor')}
                  </div>
                </div>
                {formatActivityDetails(activity)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrderActivity;